import type { UseQueryResult } from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { normalizeToISODate } from "@/lib/date-utils";

import {
  CARE_HISTORY_FAMILY_GRAPH_COLOR,
  CARE_HISTORY_FAMILY_MEMBER_GRAPH_COLOR,
  CARE_HISTORY_OOP,
  CARE_HISTORY_PERIOD_FLAG,
  CARE_HISTORY_SELF_GRAPH_COLOR,
  MONTH_NUMBER_TO_LABEL_MAPPER,
  QueryKeys,
} from "@/constants";
import type {
  CareHistoryConfigResponseType,
  CareHistoryGraphResponseType,
  CareHistoryClaimsResponseType,
  GroupedClaim,
} from "@/model/care-history";
import {
  fetchCareHistoryClaims,
  fetchCareHistoryConfig,
  fetchCareHistoryGraph,
  fetchDownloadClaims,
  fetchEOB,
} from "@/services/care-history-service";
import type {
  CareHistoryGraphItemType,
  ICareHistoryClaimsPayload,
  ICareHistoryConfigPayload,
  ICareHistoryGraphPayload,
  IEOBPayload,
} from "@/types";
import useUserInfo from "./use-user-info";
import type { PlanCoverageInfo } from "@/model/demographics";
import { filterUptoCurrentMonth } from "@/lib/care-history-utils";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { useIsFeatureEnabled } from "./use-features";
import ScreenRouteNames from "@/constants/screen-route-names";
import { queryClient } from "./use-query-client";
import type { GlobalFlag } from "@/model/global-flag";

const CONSTANTS = {
  COLOR_KEY: "color",
  MONTH_KEY: "month",
  COLOR_FAMILY_KEY: "colorFamily",
};

const useCareHistoryConfigQuery = (): UseQueryResult<
  CareHistoryConfigResponseType,
  Error
> => {
  const userInfo = useUserInfo();
  const { activeCoverage } = useSelector(
    (state: RootState) => state?.planCoverageInfo,
  );
  const configPayload: ICareHistoryConfigPayload = {
    loginGroupId: userInfo.loginGroupId,
    username: userInfo.username,
    groupId: userInfo.groupId,
    employeeId: userInfo.employeeId,
    relationship: userInfo.relationship,
    divisionId: userInfo.divisionId,
    memberId: userInfo.memberId,
    token: userInfo.accessToken,
    planId: activeCoverage.planId,
    claimCoverageCode: activeCoverage?.claimCoverageCode,
  };
  return useQuery({
    queryKey: [
      QueryKeys.CARE_HISTORY_CONFIG,
      activeCoverage?.claimCoverageCode,
    ],
    queryFn: async () => await fetchCareHistoryConfig(configPayload),
    staleTime: Infinity,
    select: (response) => {
      return response.careHistory[0];
    },
    enabled:
      Boolean(configPayload?.token) &&
      Boolean(activeCoverage?.claimCoverageCode),
  });
};

const useCareHistoryGraphQuery = (
  fromDate: string,
  toDate: string,
  periodFlag: string,
  activeCoverage: PlanCoverageInfo,
): UseQueryResult<CareHistoryGraphItemType[], Error> => {
  const userInfo = useUserInfo();
  const graphPayload: ICareHistoryGraphPayload = {
    loginGroupId: userInfo.loginGroupId,
    username: userInfo.username,
    groupId: userInfo.groupId,
    employeeId: userInfo.employeeId,
    relationship: userInfo.relationship,
    divisionId: userInfo.divisionId,
    memberId: userInfo.memberId,
    token: userInfo.accessToken,
    claimCoverageCode: activeCoverage?.claimCoverageCode,
    fromDate,
    toDate,
    periodFlag,
  };

  //global flag
  const showCareHistoryGraph =
    (
      queryClient.getQueryData([QueryKeys.GLOBAL_FLAGS, userInfo.username]) as
        | { data?: GlobalFlag[] }
        | undefined
    )?.data?.[0]?.showCareHistoryGraph ?? true;

  return useQuery({
    queryKey: [
      QueryKeys.CARE_HISTORY_GRAPH,
      graphPayload?.periodFlag,
      activeCoverage?.claimCoverageCode,
    ],
    queryFn: async () => {
      const graphResponse = await fetchCareHistoryGraph(graphPayload);
      return graphPayload?.periodFlag === CARE_HISTORY_PERIOD_FLAG.MONTHLY
        ? transformMonthlyGraphData(graphPayload?.memberId, graphResponse.data)
        : transformYearlyGraphData(graphPayload?.memberId, graphResponse.data);
    },
    staleTime: Infinity,
    enabled:
      Boolean(showCareHistoryGraph) &&
      Boolean(graphPayload?.fromDate) &&
      Boolean(graphPayload?.toDate) &&
      Boolean(graphPayload?.periodFlag),
  });
};

const useCareHistoryClaimsQuery = (
  fromDate: string,
  toDate: string,
  periodFlag: string,
  activeCoverage: PlanCoverageInfo,
): UseQueryResult<GroupedClaim[], Error> => {
  const userInfo = useUserInfo();
  const isFeatureEnabled = useIsFeatureEnabled(
    ScreenRouteNames.CARE_HISTORY.path,
  );

  const claimsPayload: ICareHistoryClaimsPayload = {
    loginGroupId: userInfo.loginGroupId,
    username: userInfo.username,
    groupId: userInfo.groupId,
    employeeId: userInfo.employeeId,
    relationship: userInfo.relationship,
    divisionId: userInfo.divisionId,
    memberId: userInfo.memberId,
    token: userInfo.accessToken,
    claimCoverageCode: activeCoverage?.claimCoverageCode,
    fromDate,
    toDate,
    periodFlag,
  };

  return useQuery({
    queryKey: [
      QueryKeys.CARE_HISTORY_CLAIMS,
      claimsPayload?.periodFlag,
      activeCoverage?.claimCoverageCode,
    ],
    queryFn: async () => {
      const claimsResponse = await fetchCareHistoryClaims(claimsPayload);
      return groupClaimsByMonth(claimsResponse.data);
    },
    staleTime: Infinity,
    enabled:
      Boolean(claimsPayload?.fromDate) &&
      Boolean(claimsPayload?.toDate) &&
      Boolean(claimsPayload?.periodFlag) &&
      isFeatureEnabled,
  });
};

function useDownloadClaims() {
  const userInfo = useUserInfo();
  const { activeCoverage } = useSelector(
    (state: RootState) => state?.planCoverageInfo,
  );

  return useMutation({
    mutationFn: (payload: {
      fromDate: string;
      toDate: string;
      periodFlag: string;
    }) =>
      fetchDownloadClaims({
        loginGroupId: userInfo.loginGroupId,
        username: userInfo.username,
        groupId: userInfo.groupId,
        employeeId: userInfo.employeeId,
        relationship: userInfo.relationship,
        divisionId: userInfo.divisionId,
        memberId: userInfo.memberId,
        token: userInfo.accessToken,
        claimCoverageCode: activeCoverage?.claimCoverageCode,
        fromDate: payload.fromDate,
        toDate: payload.toDate,
        periodFlag: payload.periodFlag,
      }),
    onSuccess: (data: {
      filename: string;
      success: boolean;
      base64Encoded: string;
      message: string;
    }) => {
      const { filename, base64Encoded, success } = data;
      if (!success) return;
      const binary = atob(base64Encoded);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    },
  });
}

const useEOBQuery = (
  claimNumber: string,
  memberId: string,
  claimCoverageCode: string,
  planId: string,
): UseQueryResult<string, Error> => {
  const userInfo = useUserInfo();
  const eobPayload: IEOBPayload = {
    divisionId: userInfo.divisionId,
    employeeId: userInfo.employeeId,
    groupId: userInfo.groupId,
    loginGroupId: userInfo.loginGroupId,
    memberId,
    planId,
    claimCoverageCode,
    token: userInfo.accessToken,
    claimNumber,
  };
  return useQuery({
    queryKey: [QueryKeys.EOB, eobPayload?.claimNumber],
    queryFn: async () => {
      const eobResponse = await fetchEOB(eobPayload);
      return eobResponse;
    },
    enabled: false,
  });
};

function transformMonthlyGraphData(
  memberId: string,
  data: Array<CareHistoryGraphResponseType>,
): CareHistoryGraphItemType[] {
  const result: Record<string, CareHistoryGraphItemType> = {};
  const familyColorMap: Record<string, string> = {};
  let colorIndex = 0;
  data.map((entry) => {
    const rawDate = entry.serviceDate.toString().padStart(6, "0");
    const year = rawDate.substring(0, 4);
    const month = rawDate.substring(4, 6);
    const key = `${year}${month}`;
    const monthLabel = MONTH_NUMBER_TO_LABEL_MAPPER[month] || "Invalid";
    const isSelf = entry.memberId === memberId;
    const memIdKey = entry.memberId;
    if (!result[key]) {
      result[key] = {
        month: monthLabel,
        allOopPaidFamily: 0,
        pharmacyOopPaidFamily: 0,
        providerOopPaidFamily: 0,
        [`${CARE_HISTORY_OOP.TOTAL_OOP_PAID}${memberId}`]: 0,
        [`${CARE_HISTORY_OOP.PHARMACY_OOP_PAID}${memberId}`]: 0,
        [`${CARE_HISTORY_OOP.MEDICAL_OOP_PAID}${memberId}`]: 0,
        [`${CONSTANTS.COLOR_KEY}${memberId}`]: CARE_HISTORY_SELF_GRAPH_COLOR,
        colorFamily: CARE_HISTORY_FAMILY_GRAPH_COLOR,
      };
    }
    const entryTotal = entry.totalOopPaid || 0;
    const entryRx = entry.rxOopPaid || 0;
    const entryMed = entry.medOopPaid || 0;
    if (isSelf) {
      result[key][`${CARE_HISTORY_OOP.TOTAL_OOP_PAID}${memberId}`] =
        (result[key][
          `${CARE_HISTORY_OOP.TOTAL_OOP_PAID}${memberId}`
        ] as number) + entryTotal;
      result[key][`${CARE_HISTORY_OOP.PHARMACY_OOP_PAID}${memberId}`] =
        (result[key][
          `${CARE_HISTORY_OOP.PHARMACY_OOP_PAID}${memberId}`
        ] as number) + entryRx;
      result[key][`${CARE_HISTORY_OOP.MEDICAL_OOP_PAID}${memberId}`] =
        (result[key][
          `${CARE_HISTORY_OOP.MEDICAL_OOP_PAID}${memberId}`
        ] as number) + entryMed;
    } else {
      // Initialize other member keys
      const keys = [
        `${CARE_HISTORY_OOP.TOTAL_OOP_PAID}${memIdKey}`,
        `${CARE_HISTORY_OOP.PHARMACY_OOP_PAID}${memIdKey}`,
        `${CARE_HISTORY_OOP.MEDICAL_OOP_PAID}${memIdKey}`,
        `${CONSTANTS.COLOR_KEY}${memIdKey}`,
      ];
      keys.forEach((k) => {
        if (!(k in result[key])) {
          if (k.startsWith(CONSTANTS.COLOR_KEY)) {
            familyColorMap[memIdKey] =
              familyColorMap[memIdKey] ||
              CARE_HISTORY_FAMILY_MEMBER_GRAPH_COLOR[colorIndex++] ||
              "#000000";
            result[key][k] = familyColorMap[memIdKey];
          } else {
            result[key][k] = 0;
          }
        }
      });
      result[key][`${CARE_HISTORY_OOP.TOTAL_OOP_PAID}${memIdKey}`] =
        (result[key][
          `${CARE_HISTORY_OOP.TOTAL_OOP_PAID}${memIdKey}`
        ] as number) + entryTotal;
      result[key][`${CARE_HISTORY_OOP.PHARMACY_OOP_PAID}${memIdKey}`] =
        (result[key][
          `${CARE_HISTORY_OOP.PHARMACY_OOP_PAID}${memIdKey}`
        ] as number) + entryRx;
      result[key][`${CARE_HISTORY_OOP.MEDICAL_OOP_PAID}${memIdKey}`] =
        (result[key][
          `${CARE_HISTORY_OOP.MEDICAL_OOP_PAID}${memIdKey}`
        ] as number) + entryMed;
      result[key].allOopPaidFamily += entryTotal;
      result[key].pharmacyOopPaidFamily += entryRx;
      result[key].providerOopPaidFamily += entryMed;
    }
  });
  return Object.values(result);
}

function transformYearlyGraphData(
  memberId: string,
  data: Array<CareHistoryGraphResponseType>,
): CareHistoryGraphItemType[] {
  const dataUptoCurrentMonth = filterUptoCurrentMonth(data);
  const transformedGraphData = transformMonthlyGraphData(
    memberId,
    dataUptoCurrentMonth,
  );
  const runningTotals: Record<string, number> = {};
  const accumulated: CareHistoryGraphItemType[] = [];
  for (const item of transformedGraphData) {
    const accumulatedItem: CareHistoryGraphItemType = {
      month: item.month,
      allOopPaidFamily: 0,
      pharmacyOopPaidFamily: 0,
      providerOopPaidFamily: 0,
      colorFamily: item.colorFamily,
    };
    for (const key in item) {
      if (key === CONSTANTS.MONTH_KEY || key === CONSTANTS.COLOR_FAMILY_KEY) {
        // Already copied above
        continue;
      }
      const val = item[key];
      if (typeof val === "number") {
        runningTotals[key] = (runningTotals[key] || 0) + val;
        accumulatedItem[key] = runningTotals[key];
      } else {
        // For non-number (string) keys except month/colorFamily, just copy as is
        accumulatedItem[key] = val;
      }
    }
    accumulated.push(accumulatedItem);
  }
  return accumulated;
}

function groupClaimsByMonth(
  claims: CareHistoryClaimsResponseType[],
): GroupedClaim[] {
  const groupedMap: Record<string, CareHistoryClaimsResponseType[]> = {};
  for (const claim of claims) {
    const key = normalizeToISODate(claim.recentSvcDate)
      .slice(0, 7)
      .replace("-", "");
    if (!groupedMap[key]) {
      groupedMap[key] = [];
    }
    groupedMap[key].push(claim);
  }
  const result: GroupedClaim[] = Object.entries(groupedMap).map(
    ([srvYrMonth, historyList]) => ({
      srvYrMonth,
      historyList,
    }),
  );
  return result.sort((a, b) => b.srvYrMonth.localeCompare(a.srvYrMonth));
}

export {
  useCareHistoryConfigQuery,
  useCareHistoryGraphQuery,
  useCareHistoryClaimsQuery,
  useEOBQuery,
  useDownloadClaims,
};
